import type { WritableSignal } from '@angular/core';
import type { Hex } from 'viem';
import type { Mon as BaseMon } from '../generated/mon';
import { Type } from '../generated/sim/Enums';

// Single-sourced from src/Enums.sol (via the transpiler). Includes the trailing
// `None` member used as the single-type sentinel.
export { Type };

export interface ExtendedMon extends Omit<BaseMon, 'stats'> {
  stats: {
    // Current battle state — signal-backed so in-place updates during a turn
    // notify OnPush components (HP bars, KO state, stamina, buffs). Writers use
    // .set()/.update(); the engine in ts-output uses its own model, so this is
    // purely the display layer.
    hp: WritableSignal<number>;
    stamina: WritableSignal<number>;
    speed: WritableSignal<number>;
    attack: WritableSignal<number>;
    defense: WritableSignal<number>;
    specialAttack: WritableSignal<number>;
    specialDefense: WritableSignal<number>;

    isKnockedOut: WritableSignal<boolean>;
    shouldSkipTurn: WritableSignal<boolean>;

    // Maximum stats — set once at construction, never mutated → stay plain.
    maxHp: number;
    maxStamina: number;
    maxSpeed: number;
    maxAttack: number;
    maxDefense: number;
    maxSpecialAttack: number;
    maxSpecialDefense: number;

    // Active status set. Replaced immutably via .update() (new Set each write)
    // so additions/removals fire CD; never .add/.delete the inner Set in place.
    status: WritableSignal<Set<Lowercase<Hex>>>;
    // Per-status extraData (hex) — turnsLeft for Sleep/Panic, degree for Burn.
    // Signal-wrapped so in-place mutations propagate to downstream computeds
    // (status-info-card.bullets()). Writers replace the Map via .update();
    // direct .set / .delete on the inner Map would not fire CD.
    statusData: WritableSignal<Map<Lowercase<Hex>, string>>;
  };
  // Optional; hydrated from getExpAndLevelsForTeam(s) at load time.
  level?: number;
  exp?: number;
  // Hydrated from getFacetData(player, monId). `unlockedFacets` is a uint12
  // bitmap (bit i → facet id i+1); `equippedFacet` is 0 (none) or 1..12.
  unlockedFacets?: number;
  equippedFacet?: number;
}

export function monStatusList(mon: ExtendedMon): LowercaseHex[] {
  return [...mon.stats.status()];
}

export interface MoveSelection {
  moveIndex: number;
  extraData: number;
  salt: bigint;
}

// Decoded GachaEvent payload; bit layout mirrors GachaTeamRegistry.sol.
export interface GachaRewards {
  points: bigint;
  expByMon: number[];
  facetBitmapByMon: number[]; // 12-bit bitmap per slot; bit i set = facet id (i+1) drawn this battle
  bonuses: number;            // bitmask — see BONUS_* in constants.ts
  multiplier: number;
  outcome: number;            // 0 loss / 1 win / 2 draw
  streakDay: number;          // 1..STREAK_FLAT_BONUS_MAX; 0 when first-game-of-day flag isn't set
}

/**
 * The signed flow uses off-chain signature exchange:
 * 1. Committer (A) signs SignedCommit and sends to B via WebSocket
 * 2. Revealer (B) signs DualSignedReveal (over A's hash + B's move) and sends to A
 * 3. Committer (A) submits executeWithDualSignedMoves() with both moves (1 TX)
 */
export enum MovePhase {
  IDLE = 'IDLE',

  // === SIGNED FLOW: COMMITTING PLAYER STATES ===
  // I selected a move, signing my commit to send to opponent
  SIGNING_COMMIT = 'SIGNING_COMMIT',
  // Signed commit sent, waiting for opponent's dual-signed reveal
  AWAITING_DUAL_REVEAL = 'AWAITING_DUAL_REVEAL',
  // Received opponent's signature, submitting executeWithDualSignedMoves TX
  EXECUTING_DUAL_SIGNED = 'EXECUTING_DUAL_SIGNED',

  // === SIGNED FLOW: REVEALING PLAYER STATES ===
  // I selected a move, waiting for committer's signed commit
  AWAITING_SIGNED_COMMIT = 'AWAITING_SIGNED_COMMIT',
  // Received committer's signature, signing dual reveal to send back
  SIGNING_DUAL_REVEAL = 'SIGNING_DUAL_REVEAL',

  // === TERMINAL STATE ===
  // Move submitted, waiting for turn to complete
  AWAITING_TURN_END = 'AWAITING_TURN_END',
}

export type LowercaseHex = Lowercase<Hex>;

export type BattleType = 'cpu' | 'pvp';
